import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A styled modal component to replace native alerts and confirmations
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message/body
 * @param {string} props.confirmLabel - Label for confirm button (default: OK)
 * @param {string} props.cancelLabel - Label for cancel button (default: Cancel)
 * @param {Function} props.onConfirm - Handler when user confirms
 * @param {Function} props.onCancel - Handler when user cancels
 * @param {string} props.type - 'alert' or 'confirm' (default: alert)
 * @param {string} props.variant - 'primary' | 'warning' | 'danger' (default: primary)
 */
export default function Modal({ 
  isOpen, 
  title, 
  message, 
  confirmLabel, 
  cancelLabel, 
  onConfirm, 
  onCancel,
  type = 'alert',
  variant = 'primary'
}) {
  const { t } = useTranslation();

  const defaultConfirmLabel = confirmLabel || t('common.ok');
  const defaultCancelLabel = cancelLabel || t('common.cancel');

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel?.();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Variant styles
  const variantStyles = {
    primary: 'from-primary-600 to-primary-500',
    success: 'from-green-600 to-green-500',
    warning: 'from-accent-500 to-orange-500',
    danger: 'from-red-600 to-red-500'
  };

  const buttonGradient = variantStyles[variant] || variantStyles.primary;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in"
      onClick={onCancel}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal content */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
          </div>
        )}
        
        {/* Body */}
        <div className="px-6 py-6">
          <p className="text-base text-gray-700 leading-relaxed">{message}</p>
        </div>
        
        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          {type === 'confirm' && (
            <button
              onClick={onCancel}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 text-gray-700 transition-all hover:scale-105 active:scale-95"
            >
              {defaultCancelLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            className={`px-6 py-3 bg-gradient-to-r ${buttonGradient} text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95`}
          >
            {defaultConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

